import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import type SdDrawer from '@solid-design-system/components/dist-versioned/components/drawer/drawer';
import { Observable, of, Subject } from 'rxjs';
import { ApiCallInjectDirective } from 'src/app/common/directives/api-call-inject.directive';
import { refetchApiSignal, toWritableApiSignal, WritableApiSignal } from 'src/app/common/util/api-signal';
import { BusinessObjectsElementService } from 'src/openapi-client';
import { FondsFilterBlockComponent } from '../fonds-filter-block/fonds-filter-block.component';
import { FondsFilterBlockData, FondsFilterSelections } from './fonds-filters';

@Component({
  selector: 'wct-fonds-filters',
  standalone: true,
  imports: [FondsFilterBlockComponent, ApiCallInjectDirective, TranslocoModule, NgTemplateOutlet],
  templateUrl: './fonds-filters.component.html',
  styleUrl: './fonds-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FondsFiltersComponent implements OnDestroy {
  @Input({ required: true })
  public set documentId(documentId: string) {
    refetchApiSignal(this.elementService.getFilters(documentId), this.filters);
  }

  @Input({ required: false }) public hiddenFilters?: Signal<string[]>;
  @Input({ required: true }) public filterSelections: WritableSignal<{
    selections: FondsFilterSelections;
  }> = signal(undefined);

  @ViewChild('drawer') protected drawer: ElementRef<SdDrawer>;

  protected readonly filters: WritableApiSignal<FondsFilterBlockData[]> = toWritableApiSignal(of(undefined));

  protected get selections(): FondsFilterSelections {
    return this.filterSelections().selections;
  }

  protected dirty: boolean = false;

  protected readonly displayedFilters: Signal<FondsFilterBlockData[]> = computed(() =>
    this.filters()
      ? this.hiddenFilters()?.length > 0
        ? this.filters().filter(filter => !this.hiddenFilters().includes(filter.title))
        : this.filters()
      : []
  );

  protected get update$(): Observable<void> {
    return this.updateSubject.asObservable();
  }

  private readonly elementService = inject(BusinessObjectsElementService);
  private readonly updateSubject: Subject<void> = new Subject();

  ngOnDestroy(): void {
    this.updateSubject.complete();
  }

  public show(): void {
    this.drawer.nativeElement.show();
    this.dirty = false;
  }

  protected apply(event: Event): void {
    if (event.target !== this.drawer.nativeElement || !this.dirty) {
      return;
    }

    for (const key of Object.keys(this.selections)) {
      const option: unknown = this.selections[key];

      if (Array.isArray(option)) {
        if (option.length < 1) {
          delete this.selections[key];
        }
      } else if (!option) {
        delete this.selections[key];
      }
    }

    this.filterSelections.set({ selections: this.selections });
  }

  protected resetAllFilters(): void {
    for (const key of Object.keys(this.selections)) {
      if (!this.hiddenFilters().includes(key)) {
        delete this.selections[key];
      }
    }

    this.dirty = true;
    this.update();
  }

  protected showFonds(event: Event) {
    this.apply(event);
    this.drawer.nativeElement.hide();
  }

  protected update(): void {
    this.updateSubject.next();
  }
}
